/**
 * Share a cellar with one other person: create a family, hand them the
 * invite code, and everything from here on — scans, cellar, questions — is
 * pooled between whoever is in it.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { api } from '../lib/api'

export default function Family() {
  const client = useQueryClient()
  const familyQuery = useQuery({ queryKey: ['family'], queryFn: api.family })
  const [inviteCode, setInviteCode] = useState('')
  const [error, setError] = useState<string | null>(null)

  const invalidate = () => client.invalidateQueries({ queryKey: ['family'] })

  const create = useMutation({
    mutationFn: api.createFamily,
    onSuccess: () => {
      setError(null)
      void invalidate()
    },
    onError: (err: Error) => setError(err.message),
  })

  const join = useMutation({
    mutationFn: () => api.joinFamily(inviteCode.trim()),
    onSuccess: () => {
      setError(null)
      setInviteCode('')
      void invalidate()
    },
    onError: (err: Error) => setError(err.message),
  })

  const leave = useMutation({
    mutationFn: api.leaveFamily,
    onSuccess: () => {
      setError(null)
      void invalidate()
    },
    onError: (err: Error) => setError(err.message),
  })

  const regenerate = useMutation({
    mutationFn: api.regenerateInviteCode,
    onSuccess: () => void invalidate(),
    onError: (err: Error) => setError(err.message),
  })

  const family = familyQuery.data?.family

  return (
    <div className="space-y-4 pt-5">
      <h2 className="font-display text-2xl">Family</h2>
      <p className="text-sm text-stone-400">
        Share your cellar with one other person. Once you're in a family together,
        the wines you've each photographed, your scans and your questions all show
        up for both of you.
      </p>

      {error && (
        <p className="rounded-xl border border-rose-800/40 bg-rose-950/60 p-3 text-sm text-rose-300">
          {error}
        </p>
      )}

      {familyQuery.isLoading && (
        <div className="card h-32 animate-pulse opacity-40" />
      )}

      {familyQuery.data && !family && (
        <div className="space-y-4">
          <div className="card space-y-3 p-5">
            <h3 className="text-sm font-medium text-stone-200">Start a family</h3>
            <p className="text-xs text-stone-500">
              You'll get an invite code to send to the person you want to share with.
            </p>
            <button
              className="btn-primary"
              disabled={create.isPending}
              onClick={() => create.mutate()}
            >
              {create.isPending ? 'Starting…' : 'Start a family'}
            </button>
          </div>

          <div className="card space-y-3 p-5">
            <h3 className="text-sm font-medium text-stone-200">Join a family</h3>
            <p className="text-xs text-stone-500">
              Enter the invite code the other person sent you.
            </p>
            <div className="flex gap-2">
              <input
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="INVITE CODE"
                maxLength={20}
                className="w-full rounded-xl border border-white/10 bg-black/20 p-3 text-sm tracking-widest text-stone-200 uppercase placeholder:text-stone-600 placeholder:normal-case focus:border-wine-500/60 focus:outline-none"
              />
              <button
                className="btn-ghost shrink-0"
                disabled={join.isPending || inviteCode.trim().length < 4}
                onClick={() => join.mutate()}
              >
                {join.isPending ? 'Joining…' : 'Join'}
              </button>
            </div>
          </div>
        </div>
      )}

      {family && (
        <div className="space-y-4">
          <div className="card space-y-4 p-5">
            <h3 className="text-sm font-medium text-stone-200">Members</h3>
            <ul className="space-y-2">
              {family.members.map((member) => (
                <li
                  key={member.uid}
                  className="flex items-center justify-between text-sm text-stone-300"
                >
                  <span>{member.displayName ?? member.email ?? 'A family member'}</span>
                </li>
              ))}
            </ul>
          </div>

          {family.isCreator && (
            <div className="card space-y-3 p-5">
              <h3 className="text-sm font-medium text-stone-200">Invite code</h3>
              <p className="text-xs text-stone-500">
                Send this to the person you want to share your cellar with.
              </p>
              <div className="flex items-center justify-between rounded-xl border border-white/10 bg-black/20 p-3">
                <span className="font-display text-xl tracking-[0.3em] text-wine-200">
                  {family.inviteCode}
                </span>
                <button
                  className="text-xs text-stone-500 hover:text-stone-300"
                  disabled={regenerate.isPending}
                  onClick={() => regenerate.mutate()}
                >
                  {regenerate.isPending ? 'Regenerating…' : 'Regenerate'}
                </button>
              </div>
            </div>
          )}

          <div className="card space-y-3 p-5">
            <button
              className="btn-ghost w-full"
              disabled={leave.isPending}
              onClick={() => {
                if (confirm('Leave this family? Your future scans and cellar activity will stop being shared.')) {
                  leave.mutate()
                }
              }}
            >
              {leave.isPending ? 'Leaving…' : 'Leave family'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
